import { TOOLTIP_STYLE } from "@/lib/chartTheme";
import { useEffect, useMemo, useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import { useRole, ROLES } from "@/data/RoleContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { RoleContextBanner } from "@/components/RoleContextBanner";
import { RoleHandoffInbox } from "@/components/RoleHandoffInbox";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { getInstallReadiness } from "@/components/JobWorkflowStatus";
import { useCrewUtilization } from "@/hooks/useCrewUtilization";
import { getInventoryAdapter, type InventorySnapshot } from "@/data/inventoryAdapter";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { useUISkin } from "@/data/UISkinContext";
import { CockpitHeader, StatStrip, type StatItem } from "@/components/dashboard/primitives";
import {
  Package, AlertTriangle, CheckCircle, Clock, ArrowRight, Zap,
  Truck, Users, ShoppingCart, BoxSelect, HardHat, Calendar,
  XCircle, TrendingDown, BarChart3, Layers, ArrowDown,
  CircleAlert, Warehouse, DollarSign,
} from "lucide-react";
import type { Job, MaterialReadiness } from "@/data/types";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";

const TODAY = new Date();
const DAY_MS = 86_400_000;

const CHART_COLORS = {
  success: "hsl(var(--chart-2))",
  warning: "hsl(var(--chart-5))",
  destructive: "hsl(var(--destructive))",
  primary: "hsl(var(--chart-1))",
  muted: "hsl(var(--muted-foreground))",
};

function getReadinessColor(status: "Ready for Install" | "Partially Ready" | "Not Ready") {
  if (status === "Ready for Install") return "text-success";
  if (status === "Partially Ready") return "text-warning";
  return "text-destructive";
}

function getReadinessBg(status: "Ready for Install" | "Partially Ready" | "Not Ready") {
  if (status === "Ready for Install") return "bg-success/10 border-success/20";
  if (status === "Partially Ready") return "bg-warning/10 border-warning/20";
  return "bg-destructive/10 border-destructive/20";
}

function getAssignee(job: Job): string {
  if (job.assignmentType === "Subcontractor") return job.assignedSubcontractor || "Unassigned";
  if (job.assignmentType === "Internal Crew") return job.assignedCrew || "Unassigned";
  return "Unassigned";
}

function getMaterialStatusIcon(readiness: MaterialReadiness) {
  switch (readiness) {
    case "Missing Items":
    case "Field Purchase Required":
      return <XCircle className="h-3.5 w-3.5 text-destructive" />;
    case "Not Ordered":
      return <ShoppingCart className="h-3.5 w-3.5 text-muted-foreground" />;
    case "Ordered":
    case "Partially Received":
      return <Clock className="h-3.5 w-3.5 text-warning" />;
    case "Fully Received":
      return <ArrowDown className="h-3.5 w-3.5 text-info" />;
    case "Staged for Job":
      return <BoxSelect className="h-3.5 w-3.5 text-primary" />;
    case "Loaded for Crew":
      return <CheckCircle className="h-3.5 w-3.5 text-success" />;
    default:
      return <Package className="h-3.5 w-3.5 text-muted-foreground" />;
  }
}

export function InstallSupplyChainCommandCenter() {
  const { jobs, issues, buildReviewEvents } = useAppData();
  const { roleInfo } = useRole();
  const { skin } = useUISkin();
  const isCockpit = skin === "cockpit";
  const openIssues = useMemo(() => issues.filter((i) => i.status === "Open" || i.status === "In Progress"), [issues]);
  const openJobs = useMemo(() => jobs.filter((j) => j.currentStage !== "Completed"), [jobs]);
  const crewMetrics = useCrewUtilization(jobs, issues);
  // Hand-offs are surfaced via the shared <RoleHandoffInbox /> below.
  // No role-specific filtering lives in this dashboard anymore.

  // ── Install Readiness Board ──
  const readinessBoard = useMemo(() => {
    const groups: Record<string, { job: Job; crew: string; daysUntil: number; blockers: string[]; status: string }[]> = {
      "Not Ready": [],
      "Partially Ready": [],
      "Ready for Install": [],
    };

    openJobs.forEach((j) => {
      const oi = openIssues.filter((i) => i.jobId === j.id);
      const r = getInstallReadiness(j, oi, buildReviewEvents);
      const daysUntil = j.scheduledInstallDate
        ? Math.round((new Date(j.scheduledInstallDate).getTime() - TODAY.getTime()) / DAY_MS)
        : 999;

      const blockers: string[] = [];
      if (j.permitRequired && j.permitStatus !== "Approved" && j.permitStatus !== "Not Required") blockers.push("Permit");
      if (j.materialReadiness === "Missing Items") blockers.push("Materials");
      if (j.materialReadiness === "Not Ordered") blockers.push("Not Ordered");
      if (!j.assignedCrew && !j.assignedSubcontractor) blockers.push("No Crew");
      if (!j.scheduledInstallDate) blockers.push("No Date");
      if (oi.length > 0) blockers.push(`${oi.length} Issue${oi.length > 1 ? "s" : ""}`);

      groups[r.status]?.push({
        job: j,
        crew: getAssignee(j),
        daysUntil,
        blockers,
        status: r.status,
      });
    });

    // Sort each group by install proximity
    Object.values(groups).forEach((g) => g.sort((a, b) => a.daysUntil - b.daysUntil));
    return groups;
  }, [openJobs, openIssues]);

  // ── Material readiness at job level ──
  const materialJobs = useMemo(() => {
    return openJobs.map((j) => {
      const materialsList = j.materialsList || [];
      const totalRequired = materialsList.reduce((s, m) => s + m.quantityRequired, 0);
      const totalAllocated = materialsList.reduce((s, m) => s + m.quantityAllocated, 0);
      const totalMissing = materialsList.reduce((s, m) => s + m.missingItems, 0);
      const fulfillment = totalRequired > 0 ? Math.round((totalAllocated / totalRequired) * 100) : (j.materialReadiness === "Loaded for Crew" || j.materialReadiness === "Staged for Job" ? 100 : 0);

      return { job: j, totalRequired, totalAllocated, totalMissing, fulfillment, materialsList };
    }).sort((a, b) => a.fulfillment - b.fulfillment);
  }, [openJobs]);

  // ── Inventory snapshot ──
  const [inventory, setInventory] = useState<InventorySnapshot | null>(null);
  useEffect(() => {
    let mounted = true;
    const adapter = getInventoryAdapter();
    const result = adapter.getSnapshot();
    if (result instanceof Promise) {
      result.then((snap) => {
        if (mounted) setInventory(snap);
      }).catch(() => {
        if (mounted) setInventory(null);
      });
    } else {
      setInventory(result);
    }
    return () => { mounted = false; };
  }, []);

  // ── Slippage & Revenue Risk ──
  const slippage = useMemo(() => {
    return openJobs
      .filter((j) => {
        if (!j.scheduledInstallDate) return false;
        const daysUntil = (new Date(j.scheduledInstallDate).getTime() - TODAY.getTime()) / DAY_MS;
        if (daysUntil < 0 || daysUntil > 7) return false;
        const oi = openIssues.filter((i) => i.jobId === j.id);
        return getInstallReadiness(j, oi, buildReviewEvents).status !== "Ready for Install";
      })
      .map((j) => {
        const reasons: string[] = [];
        if (j.materialReadiness === "Missing Items") reasons.push("Materials missing");
        if (j.permitRequired && j.permitStatus !== "Approved") reasons.push("Permit pending");
        if (!j.assignedCrew && !j.assignedSubcontractor) reasons.push("No crew assigned");
        const oi = openIssues.filter((i) => i.jobId === j.id);
        if (oi.length > 0) reasons.push(`${oi.length} open issue${oi.length > 1 ? "s" : ""}`);
        if (reasons.length === 0) reasons.push("Not fully ready");
        return {
          job: j,
          reasons,
          revenueAtRisk: getJobDisplayValue(j),
          daysUntil: Math.round((new Date(j.scheduledInstallDate).getTime() - TODAY.getTime()) / DAY_MS),
        };
      })
      .sort((a, b) => a.daysUntil - b.daysUntil);
  }, [openJobs, openIssues]);

  const totalRevenueAtRisk = slippage.reduce((s, r) => s + r.revenueAtRisk, 0);

  // ── Alerts ──
  const alerts = useMemo(() => {
    type AlertItem = { id: string; title: string; reason: string; severity: "critical" | "warning"; link: string };
    const items: AlertItem[] = [];

    // Missing materials for scheduled jobs
    openJobs.forEach((j) => {
      if (!j.scheduledInstallDate) return;
      const daysUntil = (new Date(j.scheduledInstallDate).getTime() - TODAY.getTime()) / DAY_MS;
      if (daysUntil > 5 || daysUntil < 0) return;
      if (j.materialReadiness === "Missing Items" || j.materialReadiness === "Not Ordered") {
        items.push({ id: `al-mat-${j.id}`, title: `${j.jobNumber || j.id} — ${j.customerName}`, reason: `Materials: ${j.materialReadiness} · Installs in ${Math.ceil(daysUntil)}d`, severity: daysUntil <= 2 ? "critical" : "warning", link: `/jobs/${j.id}` });
      }
    });

    // Low/out of stock
    if (inventory) {
      inventory.items.filter((i) => i.status === "Out of Stock").forEach((i) => {
        items.push({ id: `al-oos-${i.sku}`, title: `${i.itemName}`, reason: `Out of stock (${i.sku})`, severity: "critical", link: "/inventory" });
      });
      inventory.items.filter((i) => i.status === "Low Stock").forEach((i) => {
        items.push({ id: `al-low-${i.sku}`, title: `${i.itemName}`, reason: `Low stock: ${i.quantityAvailable} avail (reorder at ${i.reorderPoint})`, severity: "warning", link: "/inventory" });
      });
    }

    // Overloaded crews
    crewMetrics.filter((c) => c.status === "overloaded").forEach((c) => {
      items.push({ id: `al-crew-${c.name}`, title: c.name, reason: `Overloaded: ${c.weekJobs} jobs this week`, severity: "warning", link: "/scheduling" });
    });

    return items.sort((a, b) => (a.severity === "critical" ? -1 : 1) - (b.severity === "critical" ? -1 : 1));
  }, [openJobs, inventory, crewMetrics]);

  // Chart data
  const readinessChartData = [
    { name: "Not Ready", value: readinessBoard["Not Ready"].length, fill: CHART_COLORS.destructive },
    { name: "Partial", value: readinessBoard["Partially Ready"].length, fill: CHART_COLORS.warning },
    { name: "Ready", value: readinessBoard["Ready for Install"].length, fill: CHART_COLORS.success },
  ];

  const crewChartData = crewMetrics.slice(0, 8).map((c) => ({
    name: c.name.split(" ")[0],
    jobs: c.weekJobs,
    fill: c.status === "overloaded" ? CHART_COLORS.destructive : c.status === "optimal" ? CHART_COLORS.success : c.status === "underutilized" ? CHART_COLORS.warning : CHART_COLORS.muted,
  }));

  const cockpitIscKpis: StatItem[] = [
    { label: "Not Ready", value: readinessBoard["Not Ready"].length, accent: readinessBoard["Not Ready"].length > 0 ? "bad" : "good" },
    { label: "Partial Ready", value: readinessBoard["Partially Ready"].length, accent: readinessBoard["Partially Ready"].length > 0 ? "warn" : "good" },
    { label: "Fully Ready", value: readinessBoard["Ready for Install"].length, accent: "good" },
    { label: "Revenue at Risk", value: `$${(totalRevenueAtRisk / 1000).toFixed(0)}k`, accent: totalRevenueAtRisk > 0 ? "bad" : "good" },
    { label: "Low Stock SKUs", value: inventory ? inventory.items.filter((i) => i.status === "Low Stock" || i.status === "Out of Stock").length : 0, accent: "warn" },
    { label: "Active Crews", value: crewMetrics.filter((c) => c.status !== "idle").length },
  ];

  return (
    <div className={isCockpit ? "space-y-6 max-w-[1600px] mx-auto w-full" : "space-y-5"}>
      <RoleContextBanner />

      {/* ═══ HEADER ═══ */}
      {isCockpit ? (
        <CockpitHeader
          title="Installation & Supply Chain"
          subtitle={`Readiness · materials · inventory · crew scheduling · ${format(TODAY, "EEEE, MMMM d, yyyy")}`}
          right={
            <>
              <Link to="/inventory" className="text-[13px] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5 px-2.5 py-1.5">
                Inventory <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <Link to="/scheduling" className="text-[13px] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5 px-2.5 py-1.5">
                Calendar <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </>
          }
        />
      ) : (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-lg font-bold tracking-tight">Installation & Supply Chain Command Center</h1>
            <p className="text-xs text-muted-foreground">
              Readiness · Materials · Inventory · Crew Scheduling · {format(TODAY, "EEEE, MMMM d, yyyy")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/inventory" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
              Inventory <ArrowRight className="h-3 w-3" />
            </Link>
            <Link to="/scheduling" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
              Calendar <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      )}

      {/* ═══ ACTION REQUIRED — shared, resolver-driven hand-off inbox ═══ */}
      <RoleHandoffInbox />

      {/* ═══ ALERTS BANNER ═══ */}
      {alerts.length > 0 && (
        <Card className="border-destructive/30 bg-destructive/[0.02] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-lg bg-destructive/10 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-destructive" />
                </div>
                <div>
                  <h2 className="text-sm font-bold tracking-tight">Supply Chain Alerts</h2>
                  <p className="text-[10px] text-muted-foreground">
                    {alerts.filter((a) => a.severity === "critical").length} critical · {alerts.filter((a) => a.severity === "warning").length} warnings
                  </p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {alerts.slice(0, 6).map((item) => (
                <Link key={item.id} to={item.link}
                  className={`group flex items-start gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/40 ${
                    item.severity === "critical" ? "border-destructive/25 bg-destructive/[0.03]" : "border-warning/25 bg-warning/[0.03]"
                  }`}>
                  <div className={`mt-0.5 h-2 w-2 rounded-full shrink-0 ${item.severity === "critical" ? "bg-destructive" : "bg-warning"}`} />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-semibold truncate block">{item.title}</span>
                    <p className="text-[10px] text-muted-foreground">{item.reason}</p>
                  </div>
                  <ArrowRight className="h-3 w-3 text-muted-foreground/40 group-hover:text-primary shrink-0 mt-1" />
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ═══ KPI STRIP ═══ */}
      {isCockpit ? (
        <StatStrip items={cockpitIscKpis} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {[
            { label: "Not Ready", value: readinessBoard["Not Ready"].length, icon: XCircle, color: "text-destructive" },
            { label: "Partial Ready", value: readinessBoard["Partially Ready"].length, icon: AlertTriangle, color: "text-warning" },
            { label: "Fully Ready", value: readinessBoard["Ready for Install"].length, icon: CheckCircle, color: "text-success" },
            { label: "Revenue at Risk", value: `$${(totalRevenueAtRisk / 1000).toFixed(0)}k`, icon: TrendingDown, color: totalRevenueAtRisk > 0 ? "text-destructive" : "text-success" },
            { label: "Low Stock SKUs", value: inventory ? inventory.items.filter((i) => i.status === "Low Stock" || i.status === "Out of Stock").length : 0, icon: Package, color: "text-warning" },
            { label: "Active Crews", value: crewMetrics.filter((c) => c.status !== "idle").length, icon: Users, color: "text-primary" },
          ].map((s) => (
            <Card key={s.label} className="shadow-none border h-full">
              <CardContent className="p-3 text-center">
                <s.icon className={`h-4 w-4 mx-auto mb-1 ${s.color}`} />
                <p className={`text-xl font-bold leading-tight ${s.color}`}>{s.value}</p>
                <p className="text-[9px] text-muted-foreground font-medium mt-0.5 uppercase tracking-wider">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ═══ TABBED SECTIONS ═══ */}
      <Tabs defaultValue="readiness">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="readiness" className="text-xs gap-1"><CheckCircle className="h-3 w-3" /> Readiness</TabsTrigger>
          <TabsTrigger value="materials" className="text-xs gap-1"><Package className="h-3 w-3" /> Materials</TabsTrigger>
          <TabsTrigger value="inventory" className="text-xs gap-1"><Warehouse className="h-3 w-3" /> Inventory</TabsTrigger>
          <TabsTrigger value="crews" className="text-xs gap-1"><Users className="h-3 w-3" /> Crews</TabsTrigger>
          <TabsTrigger value="slippage" className="text-xs gap-1"><TrendingDown className="h-3 w-3" /> Slippage</TabsTrigger>
          <TabsTrigger value="lifecycle" className="text-xs gap-1"><Layers className="h-3 w-3" /> Material Flow</TabsTrigger>
        </TabsList>

        {/* ──── READINESS BOARD ──── */}
        <TabsContent value="readiness" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-primary" /> Readiness Distribution
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={readinessChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {readinessChartData.map((d, i) => (
                          <Cell key={i} fill={d.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Jobs needing attention - immediate */}
            <Card>
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <CircleAlert className="h-4 w-4 text-destructive" /> Upcoming Installs at Risk
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-1 max-h-52 overflow-y-auto">
                  {[...readinessBoard["Not Ready"], ...readinessBoard["Partially Ready"]]
                    .filter((r) => r.daysUntil >= 0 && r.daysUntil <= 7)
                    .slice(0, 8)
                    .map((r) => (
                      <Link key={r.job.id} to={`/jobs/${r.job.id}`}
                        className={`flex items-center justify-between p-2 rounded-md border transition-colors hover:bg-muted/40 ${getReadinessBg(r.status as any)}`}>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold truncate">{r.job.customerName}</p>
                          <p className="text-[10px] text-muted-foreground">{r.job.id} · {r.crew}</p>
                        </div>
                        <div className="text-right shrink-0 ml-2">
                          <p className="text-[10px] font-bold">{r.daysUntil <= 0 ? "Today" : `${r.daysUntil}d`}</p>
                          <div className="flex gap-1 flex-wrap justify-end">
                            {r.blockers.slice(0, 2).map((b) => (
                              <span key={b} className="text-[8px] px-1 py-0.5 rounded bg-destructive/10 text-destructive font-medium">{b}</span>
                            ))}
                          </div>
                        </div>
                      </Link>
                    ))}
                  {[...readinessBoard["Not Ready"], ...readinessBoard["Partially Ready"]].filter((r) => r.daysUntil >= 0 && r.daysUntil <= 7).length === 0 && (
                    <div className="py-6 text-center">
                      <CheckCircle className="h-6 w-6 text-success mx-auto mb-1" />
                      <p className="text-xs text-muted-foreground">All upcoming installs are ready</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Full readiness table */}
          <Card>
            <CardHeader className="pb-2 px-4 pt-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">Install Readiness Board</CardTitle>
                <Link to="/jobs" className="text-[10px] text-primary hover:underline inline-flex items-center gap-1">All Jobs <ArrowRight className="h-3 w-3" /></Link>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 font-semibold text-muted-foreground">Job</th>
                      <th className="pb-2 font-semibold text-muted-foreground">Customer</th>
                      <th className="pb-2 font-semibold text-muted-foreground">Install Date</th>
                      <th className="pb-2 font-semibold text-muted-foreground">Crew</th>
                      <th className="pb-2 font-semibold text-muted-foreground">Status</th>
                      <th className="pb-2 font-semibold text-muted-foreground">Blockers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...readinessBoard["Not Ready"], ...readinessBoard["Partially Ready"], ...readinessBoard["Ready for Install"]]
                      .slice(0, 20)
                      .map((r) => (
                        <tr key={r.job.id} className="border-b border-border/30 hover:bg-muted/20">
                          <td className="py-2"><Link to={`/jobs/${r.job.id}`} className="text-primary hover:underline font-medium">{r.job.id}</Link></td>
                          <td className="py-2">{r.job.customerName}</td>
                          <td className="py-2">{r.job.scheduledInstallDate || "—"}</td>
                          <td className="py-2">{r.crew}</td>
                          <td className="py-2">
                            <Badge variant="outline" className={`text-[9px] ${getReadinessColor(r.status as any)}`}>{r.status}</Badge>
                          </td>
                          <td className="py-2">
                            <div className="flex gap-1 flex-wrap">
                              {r.blockers.map((b) => (
                                <span key={b} className="text-[8px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">{b}</span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ──── MATERIALS ──── */}
        <TabsContent value="materials" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Package className="h-4 w-4 text-primary" /> Job Material Readiness
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 font-semibold text-muted-foreground">Job</th>
                      <th className="pb-2 font-semibold text-muted-foreground">Customer</th>
                      <th className="pb-2 font-semibold text-muted-foreground">Readiness</th>
                      <th className="pb-2 font-semibold text-muted-foreground text-right">Required</th>
                      <th className="pb-2 font-semibold text-muted-foreground text-right">Allocated</th>
                      <th className="pb-2 font-semibold text-muted-foreground text-right">Missing</th>
                      <th className="pb-2 font-semibold text-muted-foreground text-right">Fill %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialJobs.slice(0, 20).map((mj) => (
                      <tr key={mj.job.id} className="border-b border-border/30 hover:bg-muted/20">
                        <td className="py-2"><Link to={`/jobs/${mj.job.id}`} className="text-primary hover:underline font-medium">{mj.job.id}</Link></td>
                        <td className="py-2">{mj.job.customerName}</td>
                        <td className="py-2 flex items-center gap-1.5">
                          {getMaterialStatusIcon(mj.job.materialReadiness)}
                          <span className="text-[10px]">{mj.job.materialReadiness}</span>
                        </td>
                        <td className="py-2 text-right">{mj.totalRequired || "—"}</td>
                        <td className="py-2 text-right">{mj.totalAllocated || "—"}</td>
                        <td className="py-2 text-right text-destructive">{mj.totalMissing > 0 ? mj.totalMissing : "—"}</td>
                        <td className={`py-2 text-right font-medium ${mj.fulfillment >= 100 ? "text-success" : mj.fulfillment > 50 ? "text-warning" : "text-destructive"}`}>
                          {mj.fulfillment}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ──── INVENTORY ──── */}
        <TabsContent value="inventory" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-2 px-4 pt-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Warehouse className="h-4 w-4 text-primary" /> Stock Overview
                </CardTitle>
                <Link to="/inventory" className="text-[10px] text-primary hover:underline inline-flex items-center gap-1">
                  Full Inventory <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {inventory ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-2 font-semibold text-muted-foreground">SKU</th>
                        <th className="pb-2 font-semibold text-muted-foreground">Item</th>
                        <th className="pb-2 font-semibold text-muted-foreground text-right">On-Hand</th>
                        <th className="pb-2 font-semibold text-muted-foreground text-right">Reserved</th>
                        <th className="pb-2 font-semibold text-muted-foreground text-right">Available</th>
                        <th className="pb-2 font-semibold text-muted-foreground text-right">Reorder</th>
                        <th className="pb-2 font-semibold text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inventory.items
                        .sort((a, b) => {
                          const order: Record<string, number> = { "Out of Stock": 0, "Low Stock": 1, "In Stock": 2 };
                          return (order[a.status] ?? 3) - (order[b.status] ?? 3);
                        })
                        .slice(0, 20)
                        .map((item) => (
                          <tr key={item.sku} className="border-b border-border/30 hover:bg-muted/20">
                            <td className="py-2 font-mono text-[10px]">{item.sku}</td>
                            <td className="py-2">{item.itemName}</td>
                            <td className="py-2 text-right">{item.quantityOnHand}</td>
                            <td className="py-2 text-right">{item.quantityReserved}</td>
                            <td className="py-2 text-right font-medium">{item.quantityAvailable}</td>
                            <td className="py-2 text-right text-muted-foreground">{item.reorderPoint}</td>
                            <td className="py-2">
                              <Badge
                                variant={item.status === "Out of Stock" ? "destructive" : item.status === "Low Stock" ? "secondary" : "default"}
                                className="text-[9px]"
                              >
                                {item.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground py-4 text-center">Inventory data unavailable</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ──── CREWS ──── */}
        <TabsContent value="crews" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-primary" /> Crew Workload (This Week)
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={crewChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="jobs" radius={[4, 4, 0, 0]}>
                        {crewChartData.map((d, i) => (
                          <Cell key={i} fill={d.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 px-4 pt-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" /> Crew Status
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-1.5 max-h-52 overflow-y-auto">
                  {crewMetrics.sort((a, b) => b.weekJobs - a.weekJobs).map((c) => (
                    <div key={c.name} className="flex items-center justify-between p-2 rounded-md border bg-muted/10">
                      <div>
                        <p className="text-xs font-semibold">{c.name}</p>
                        <p className="text-[10px] text-muted-foreground">{c.type} · {c.todayJobs} today · {c.weekJobs} this week</p>
                      </div>
                      <Badge
                        variant={c.status === "overloaded" ? "destructive" : c.status === "optimal" ? "default" : "secondary"}
                        className="text-[9px]"
                      >
                        {c.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ──── SLIPPAGE ──── */}
        <TabsContent value="slippage" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <TrendingDown className="h-4 w-4 text-destructive" /> Revenue at Risk — Next 7 Days
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {slippage.length > 0 ? (
                <>
                  <div className="p-3 rounded-lg bg-destructive/5 border border-destructive/15 mb-4 flex items-center gap-4">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Total at Risk</p>
                      <p className="text-2xl font-bold text-destructive">${(totalRevenueAtRisk / 1000).toFixed(0)}k</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Jobs Affected</p>
                      <p className="text-2xl font-bold">{slippage.length}</p>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {slippage.map((s) => (
                      <Link key={s.job.id} to={`/jobs/${s.job.id}`}
                        className="flex items-center justify-between p-2.5 rounded-md border hover:bg-muted/40 transition-colors">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold">{s.job.customerName}</p>
                          <p className="text-[10px] text-muted-foreground">{s.job.id} · {s.reasons.join(" · ")}</p>
                        </div>
                        <div className="text-right shrink-0 ml-2">
                          <p className="text-xs font-bold text-destructive">${s.revenueAtRisk.toLocaleString()}</p>
                          <p className="text-[10px] text-muted-foreground">{s.daysUntil <= 0 ? "Today" : `${s.daysUntil}d away`}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                <div className="py-8 text-center">
                  <CheckCircle className="h-8 w-8 text-success mx-auto mb-2" />
                  <p className="text-sm font-medium text-success">No slippage risk detected</p>
                  <p className="text-[10px] text-muted-foreground">All upcoming installs are on track</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ──── MATERIAL FLOW ──── */}
        <TabsContent value="lifecycle" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Layers className="h-4 w-4 text-primary" /> Material Lifecycle Tracking
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {(() => {
                const stages = [
                  { label: "Not Ordered", count: openJobs.filter((j) => j.materialReadiness === "Not Ordered").length, icon: ShoppingCart, color: "text-muted-foreground" },
                  { label: "Ordered", count: openJobs.filter((j) => j.materialReadiness === "Ordered").length, icon: Truck, color: "text-info" },
                  { label: "Partially Received", count: openJobs.filter((j) => j.materialReadiness === "Partially Received").length, icon: ArrowDown, color: "text-warning" },
                  { label: "Fully Received", count: openJobs.filter((j) => j.materialReadiness === "Fully Received").length, icon: Package, color: "text-primary" },
                  { label: "Staged for Job", count: openJobs.filter((j) => j.materialReadiness === "Staged for Job").length, icon: BoxSelect, color: "text-primary" },
                  { label: "Loaded for Crew", count: openJobs.filter((j) => j.materialReadiness === "Loaded for Crew").length, icon: CheckCircle, color: "text-success" },
                  { label: "Missing Items", count: openJobs.filter((j) => j.materialReadiness === "Missing Items").length, icon: XCircle, color: "text-destructive" },
                ];

                return (
                  <div className="space-y-4">
                    {/* Flow pipeline */}
                    <div className="flex items-center gap-1 overflow-x-auto pb-2">
                      {stages.filter((s) => s.label !== "Missing Items").map((stage, i, arr) => (
                        <div key={stage.label} className="flex items-center gap-1">
                          <div className="flex flex-col items-center min-w-[80px]">
                            <div className={`h-10 w-10 rounded-full border-2 flex items-center justify-center ${stage.count > 0 ? "border-primary bg-primary/10" : "border-muted bg-muted/20"}`}>
                              <span className="text-sm font-bold">{stage.count}</span>
                            </div>
                            <p className="text-[8px] text-muted-foreground font-medium mt-1 text-center uppercase leading-tight">{stage.label}</p>
                          </div>
                          {i < arr.length - 1 && <ArrowRight className="h-3 w-3 text-muted-foreground/40 shrink-0 mb-4" />}
                        </div>
                      ))}
                    </div>

                    {/* Missing items callout */}
                    {stages.find((s) => s.label === "Missing Items")!.count > 0 && (
                      <div className="p-3 rounded-lg border border-destructive/20 bg-destructive/5 flex items-center gap-3">
                        <XCircle className="h-5 w-5 text-destructive shrink-0" />
                        <div>
                          <p className="text-xs font-bold text-destructive">
                            {stages.find((s) => s.label === "Missing Items")!.count} jobs with missing items
                          </p>
                          <p className="text-[10px] text-muted-foreground">Require immediate procurement action</p>
                        </div>
                        <Link to="/warehouse-readiness" className="ml-auto text-[10px] text-primary hover:underline shrink-0">
                          Resolve <ArrowRight className="h-3 w-3 inline" />
                        </Link>
                      </div>
                    )}

                    {/* Detailed stage list */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                      {stages.map((stage) => (
                        <div key={stage.label} className="p-3 rounded-lg border bg-muted/10 text-center">
                          <stage.icon className={`h-4 w-4 mx-auto mb-1 ${stage.color}`} />
                          <p className="text-xl font-bold">{stage.count}</p>
                          <p className="text-[9px] text-muted-foreground font-medium uppercase tracking-wider">{stage.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
